import {
  Button, Card, DateInput, ErrorState, Field, Input, LARGEST_PAGE, PageHeader, Permissions, PhotoModeration, Row, Select,
  Stack, invalidDateIn, todayIso, useApi, useI18n, usePermission,
  type PhotoStatus,
} from '@daghfal/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';

const STATUSES: PhotoStatus[] = ['Published', 'Withdrawn', 'Pending'];

/**
 * The gallery and its moderation (GAPS 3.14): the photographs by class, day and occasion, each retagged, recaptioned,
 * deleted or, once withdrawn, published again (decision 2026-10-28 #4), and the occasions they are grouped under.
 *
 * Withdrawn and pending pictures are listed only to someone who may publish them; the API refuses the rest.
 */
export function Photos() {
  const api = useApi();
  const { t } = useI18n();
  const mayPublish = usePermission(Permissions.photos.publish);
  const [classId, setClassId] = useState<number | ''>('');
  const [occasionId, setOccasionId] = useState<number | ''>('');
  const [status, setStatus] = useState<PhotoStatus>('Published');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');

  const classes = useQuery({
    queryKey: ['classes', { status: 'Active', pageSize: LARGEST_PAGE }],
    queryFn: () => api.classes.list({ status: 'Active', pageSize: LARGEST_PAGE }),
  });
  const occasions = useQuery({ queryKey: ['occasions'], queryFn: () => api.photos.occasions() });

  // The children a picture may be retagged with: the class's, so the list stays short enough to choose from.
  const children = useQuery({
    queryKey: ['children', { classId, status: 'Active', pageSize: LARGEST_PAGE }],
    queryFn: () => api.children.list({ classId: classId as number, status: 'Active', pageSize: LARGEST_PAGE }),
    enabled: classId !== '',
  });

  const query = {
    ...(classId === '' ? {} : { classId }),
    ...(occasionId === '' ? {} : { occasionId }),
    ...(from ? { from } : {}),
    ...(to ? { to } : {}),
    status,
    pageSize: LARGEST_PAGE,
  };

  return (
    <Stack>
      <PageHeader title={t('photos.title')} lede={t('photos.lede')} />

      <Row className="dg-row--wrap">
        <Select aria-label={t('class.one')} value={classId}
          onChange={(event) => setClassId(event.target.value === '' ? '' : Number(event.target.value))}>
          <option value="">{t('photos.allClasses')}</option>
          {(classes.data?.items ?? []).map((klass) => <option key={klass.id} value={klass.id}>{klass.name}</option>)}
        </Select>
        <Select aria-label={t('photos.occasion')} value={occasionId}
          onChange={(event) => setOccasionId(event.target.value === '' ? '' : Number(event.target.value))}>
          <option value="">{t('photos.allOccasions')}</option>
          {(occasions.data ?? []).map((occasion) => <option key={occasion.id} value={occasion.id}>{occasion.name}</option>)}
        </Select>
        {mayPublish && (
          <Select aria-label={t('photos.statusFilter')} value={status}
            onChange={(event) => setStatus(event.target.value as PhotoStatus)}>
            {STATUSES.map((value) => <option key={value} value={value}>{t(`photos.status.${value}`)}</option>)}
          </Select>
        )}
        <DateInput aria-label={t('photos.from')} value={from} onChange={setFrom} />
        <DateInput aria-label={t('photos.to')} value={to} onChange={setTo} />
      </Row>

      {classes.isError && <ErrorState error={classes.error} onRetry={() => void classes.refetch()} />}

      <PhotoModeration query={query} candidates={children.data?.items ?? []} />

      <OccasionForm />
    </Stack>
  );
}

/** Creates an occasion photographs can be grouped under: a trip, a party, a national day. */
function OccasionForm() {
  const api = useApi();
  const { t, settings } = useI18n();
  const queryClient = useQueryClient();
  const mayCreate = usePermission(Permissions.photos.upload);
  const [name, setName] = useState('');
  const [date, setDate] = useState(todayIso(settings));
  const [typeId, setTypeId] = useState<number | ''>('');

  const types = useQuery({ queryKey: ['catalog', 'Occasion'], queryFn: () => api.catalog.list({ type: 'Occasion' }) });

  const create = useMutation({
    mutationFn: () => api.photos.createOccasion({ name: name.trim(), date, typeCatalogItemId: typeId === '' ? null : typeId }),
    onSuccess: async () => {
      setName('');
      setTypeId('');
      await queryClient.invalidateQueries({ queryKey: ['occasions'] });
    },
  });

  if (!mayCreate) return null;

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (invalidDateIn(event.currentTarget)) return;
    if (name.trim() && date) create.mutate();
  };

  return (
    <Card title={t('occasions.new')} hint={t('occasions.hint')}>
      <form onSubmit={submit} noValidate>
        <Stack tight>
          {create.isError && <ErrorState error={create.error} />}
          {create.isSuccess && <span className="dg-card__hint">{t('occasions.created')}</span>}
          <Field label={t('occasions.name')} required>
            {(props) => <Input {...props} value={name} onChange={(event) => setName(event.target.value)} />}
          </Field>
          <Field label={t('occasions.date')} required>
            {(props) => <DateInput {...props} value={date} onChange={setDate} />}
          </Field>
          <Field label={t('occasions.type')}>
            {(props) => (
              <Select {...props} value={typeId}
                onChange={(event) => setTypeId(event.target.value === '' ? '' : Number(event.target.value))}>
                <option value="">—</option>
                {(types.data ?? []).filter((item) => item.isActive).map((item) => (
                  <option key={item.id} value={item.id}>{item.nameAr}</option>
                ))}
              </Select>
            )}
          </Field>
          <Row>
            <Button type="submit" busy={create.isPending} busyLabel={t('action.saving')} disabled={!name.trim() || !date}>
              {t('occasions.create')}
            </Button>
          </Row>
        </Stack>
      </form>
    </Card>
  );
}
